import React, { useState, useMemo, useRef } from 'react';
import { FileText, Upload, Download, Trash2, Search, FolderOpen, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
export default function Documenten() {

  const { t } = useT();  const { documenten, deleteDocument, refreshAll } = useData();
  const [klantFilterInit, setKlantFilterInit] = useState(() => {
    const kid = sessionStorage.getItem('documenten:filterKlantId');
    const knaam = sessionStorage.getItem('documenten:filterKlantNaam');
    if (kid) {
      sessionStorage.removeItem('documenten:filterKlantId');
      sessionStorage.removeItem('documenten:filterKlantNaam');
      return { id: kid, naam: knaam || '' };
    }
    return { id: '', naam: '' };
  });
  const [filter, setFilter] = useState(klantFilterInit.naam || '');
  const [typeFilter, setTypeFilter] = useState('Alle');
  const [klantFilter, setKlantFilter] = useState(klantFilterInit);
  const fileInputRef = useRef(null);
  // Voorkom dat klantFilterInit lint waarschuwt als unused
  void setKlantFilterInit;

  const types = useMemo(() => ['Alle', ...Array.from(new Set(documenten.map(d => d.type)))], [documenten]);

  const handleImport = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const type = prompt('Type document (Factuur / Offerte / Werkbon / Foto / Contract / etc):', 'Document') || 'Document';
    const klant = prompt('Klant (optioneel):', '-') || '-';
    const kenteken = prompt('Kenteken (optioneel):', '') || '';
    try {
      toast.info(`Uploaden van ${file.name}...`);
      const result = await carsysApi.uploadDocument(file, { type, klant, kenteken });
      const cloudInfo = Object.keys(result.cloudUrls || {}).length > 0 ? ` + cloud sync naar ${Object.keys(result.cloudUrls).join(', ')}` : '';
      toast.success(`Geüpload: ${file.name}${cloudInfo}`);
      refreshAll();
    } catch (e) {
      toast.error(t('Upload mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async (d) => {
    if (!(await confirmDialog(`Document '${d.naam}' verwijderen?`))) return;
    try { await deleteDocument(d.id); toast.success(t('Document verwijderd')); }
    catch { toast.error(t('Mislukt')); }
  };

  const handleDownload = async (d) => {
    if (!d.storagePath) {
      toast.info(t('Dit is mock-data zonder echt bestand. Upload nieuwe documenten voor echte downloads.'));
      return;
    }
    try {
      const tok = localStorage.getItem('monteurcentraal_token');
      const r = await fetch(carsysApi.downloadDocumentUrl(d.id), { headers: { Authorization: `Bearer ${tok}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = d.naam || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('Download mislukt'), { description: e.message });
    }
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return documenten.filter(d => (!f || d.naam.toLowerCase().includes(f) || d.klant.toLowerCase().includes(f) || d.kenteken.toLowerCase().includes(f)) && (typeFilter === 'Alle' || d.type === typeFilter));
  }, [documenten, filter, typeFilter]);

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.documents.title')}</h2><p>{documenten.length} {t('documenten in archief', 'documenten in archief')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn primary" onClick={handleImport}><Upload size={14} /> {t('Uploaden')}</button>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" />
          <button className="cs-btn"><FolderOpen size={14} /> {t('Map openen')}</button>
        </div>
      </div>

      <div className="cs-toolbar">
        {klantFilter.naam && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--cs-accent-soft)', color: 'var(--cs-accent)', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600 }} data-testid="documenten-klant-filter">
            Filter: {klantFilter.naam}
            <button className="cs-btn ghost" style={{ padding: 2 }} onClick={() => { setKlantFilter({ id: '', naam: '' }); setFilter(''); }} title="Filter wissen"><X size={12} /></button>
          </div>
        )}
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek op naam, klant of kenteken...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {types.map(t => (
            <button key={t} className="cs-btn ghost" style={{ padding: '4px 12px', background: typeFilter === t ? 'var(--cs-accent-soft)' : 'transparent', color: typeFilter === t ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setTypeFilter(t)}>{t}</button>
          ))}
        </div>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr><th>{t('Naam')}</th><th>{t('Type')}</th><th>{t('Klant')}</th><th>{t('Kenteken')}</th><th>{t('Datum')}</th><th>{t('Grootte')}</th><th></th></tr></thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id}>
                <td><FileText size={14} style={{ verticalAlign: 'middle', color: 'var(--cs-accent)', marginRight: 6 }} /><span style={{ fontWeight: 600 }}>{d.naam}</span></td>
                <td><span className="cs-badge blue">{d.type}</span></td>
                <td>{d.klant}</td>
                <td style={{ fontFamily: 'ui-monospace, monospace' }}>{d.kenteken}</td>
                <td>{new Date(d.datum).toLocaleDateString('nl-NL')}</td>
                <td style={{ color: 'var(--cs-text-muted)' }}>{d.grootte}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="cs-btn ghost" onClick={() => handleDownload(d)}><Download size={12} /></button>
                  <button className="cs-btn ghost danger" onClick={() => handleDelete(d)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="cs-empty">{t('Geen documenten gevonden.', 'Geen documenten gevonden.')}</div>}
      </div>
    </div>
  );
}
